import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
	Button,
	TextField,
	Box,
	Typography,
	InputAdornment,
	IconButton,
	Alert,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { osName } from 'react-device-detect'
import axios from 'axios'
import {
	getCurrentUser,
	getUserDeviceInfo,
	removeCurrentUser,
	setCurrentUser,
	setUserDeviceInfo,
} from 'helpers/storage'
import userInfo, { removeUserInfo, setUserInfo } from 'hooks/userInfo'
import { useDispatch } from 'react-redux'
import { setCmmnCd } from 'hooks/cmmnCodeFactory'
import { alertModal, confirmAlert } from '../../util/util'
import { styled } from '@mui/material/styles'
import { UserModel, Kakao, DeviceInfoModel } from 'constants/types'
import PersonIcon from '@mui/icons-material/Person'
import LockIcon from '@mui/icons-material/Lock'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Link } from 'react-router-dom'
import {
	LoginContainer,
	GridBackground,
	LoginPaper,
} from 'components/ApplicationStyles'
import instance from 'api/instance'
import { apiLogout } from 'api/api'

declare global {
	interface Window {
		Kakao: Kakao
	}
}

// 상수 정의
const DEVICE_TYPE_MAP = {
	Windows: 'DEVICE_TYPE_WINDOWS',
	'Mac OS': 'DEVICE_TYPE_MACOS',
	Android: 'DEVICE_TYPE_ANDROID',
	iOS: 'DEVICE_TYPE_IOS',
} as const

const API_ENDPOINTS = {
	LOGIN: `${process.env.REACT_APP_HOST}/auth/login`,
	USER_INFO: `${process.env.REACT_APP_HOST}/user/me`,
	COMMON_CODE: `${process.env.REACT_APP_HOST}/cmmn/codeList`,
	DEVICE_INFO_UPDATE: `${process.env.REACT_APP_HOST}/user/update/device`,
	TERM: `${process.env.REACT_APP_HOST}/shop/site/info`,
} as const

// 타입 정의
interface LoginRequest {
	username: string
	password: string
	deviceInfo: {
		deviceId: string
		deviceType: string
		notificationToken: string
	}
}

// LoginResponse는 UserModel과 동일한 구조
type LoginResponse = UserModel

const TechTextField = styled(TextField)(({ theme }) => ({
	'& .MuiOutlinedInput-root': {
		background: 'rgba(255, 255, 255, 0.05)',
		borderRadius: '12px',
		color: 'white',
		transition: 'all 0.3s ease',
		'& fieldset': {
			borderColor: 'rgba(255, 255, 255, 0.2)',
			borderWidth: '1px',
		},
		'&:hover fieldset': {
			borderColor: '#4C51D3',
			boxShadow: '0 0 10px rgba(76, 81, 211, 0.3)',
		},
		'&.Mui-focused fieldset': {
			borderColor: '#4C51D3',
			borderWidth: '2px',
			boxShadow: '0 0 15px rgba(76, 81, 211, 0.5)',
		},
	},
	'& .MuiInputLabel-root': {
		color: 'rgba(255, 255, 255, 0.7)',
		'&.Mui-focused': {
			color: '#4C51D3',
		},
	},
	'& .MuiInputBase-input': {
		color: 'white',
	},
	'& .MuiInputAdornment-root': {
		color: 'rgba(255, 255, 255, 0.7)',
	},
}))

const TechTypography = styled(Typography)(({ theme }) => ({
	background: 'linear-gradient(45deg, #4C51D3, #7184AD)',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
	fontWeight: 700,
	textAlign: 'center',
	marginBottom: theme.spacing(3),
}))

const AccountContainer = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	margin: 0,
	padding: 0,
	'& a': {
		color: 'rgba(255, 255, 255, 0.7)',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
})

const Login: React.FC = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	// State 관리
	const [showPassword, setShowPassword] = useState(false)
	const [loginRequest, setLoginRequest] = useState<LoginRequest>({
		username: '',
		password: '',
		deviceInfo: {
			deviceId: uuidv4(),
			deviceType:
				DEVICE_TYPE_MAP[osName as keyof typeof DEVICE_TYPE_MAP] || 'OTHER',
			notificationToken: uuidv4(),
		},
	})
	const [errorMessage, setErrorMessage] = useState('')
	const [isError, setIsError] = useState(false)
	const [submitDisabled, setSubmitDisabled] = useState(false)

	// 카카오 SDK 초기화
	useEffect(() => {
		const script = document.createElement('script')
		script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.7/kakao.min.js'
		script.async = true

		script.onload = () => {
			if (window.Kakao && !window.Kakao.isInitialized()) {
				window.Kakao.init('5beaea23dadceea3de27e7686e9304d5')
			}
		}

		script.onerror = () => {
			console.error('카카오 SDK 로드 실패')
		}

		document.head.appendChild(script)

		return () => {
			// cleanup: script 태그 제거
			const existingScript = document.querySelector(
				'script[src="https://t1.kakaocdn.net/kakao_js_sdk/2.7.7/kakao.min.js"]',
			)
			if (existingScript) {
				document.head.removeChild(existingScript)
			}
		}
	}, [])

	// 에러 상태 초기화
	const clearError = useCallback(() => {
		setIsError(false)
		setErrorMessage('')
	}, [])

	// Input 변경 핸들러 최적화
	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = e.target
			clearError() // 입력 시 에러 상태 초기화
			setLoginRequest((prev) => ({
				...prev,
				[name]: value.trim(),
			}))
		},
		[clearError],
	)

	// 비밀번호 표시 토글
	const togglePasswordVisibility = useCallback(() => {
		setShowPassword((prev) => !prev)
	}, [])

	// 카카오 로그인 처리
	const handleKakaoLogin = useCallback(() => {
		try {
			const clientId = process.env.REACT_APP_KAKAO_APP_KEY
			const redirectUri = encodeURIComponent(
				process.env.REACT_APP_KAKAO_REDIRECT_URI || '',
			)
			const state = Math.random().toString(36).substring(2, 15)
			// 카카오 로그인 URL 생성 (팝업용)
			const kakaoLoginUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&state=${state}`

			// 카카오 로그인 팝업 열기
			const popup = window.open(
				kakaoLoginUrl,
				'kakaoLogin',
				'width=600,height=700,scrollbars=yes,resizable=yes',
			)

			if (!popup) {
				throw new Error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.')
			}

			console.log(popup)

			console.log('카카오 로그인 팝업 열림')
		} catch (error) {
			console.error('카카오 로그인 오류:', error)
			setErrorMessage('카카오 로그인을 실행할 수 없습니다.')
			setIsError(true)
		}
	}, [])

	// 네이버 로그인 처리
	const handleNaverLogin = useCallback(() => {
		try {
			const clientId = process.env.REACT_APP_NAVER_CLIENT_ID
			const redirectUri = encodeURIComponent(
				process.env.REACT_APP_NAVER_REDIRECT_URI || '',
			)
			const state = Math.random().toString(36).substring(2, 15)

			// 네이버 로그인 URL 생성
			const naverLoginUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}`

			// 네이버 로그인 페이지로 이동 (팝업으로 처리)
			const popup = window.open(naverLoginUrl, '_blank', 'width=600,height=700')
			if (!popup) {
				throw new Error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.')
			}
			console.log(popup)
		} catch (error) {
			console.error('네이버 로그인 오류:', error)
			setErrorMessage('네이버 로그인을 실행할 수 없습니다.')
			setIsError(true)
		}
	}, [])

	// 구글 로그인 처리
	const handleGoogleLogin = useCallback(() => {
		try {
			const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID
			const redirectUri = encodeURIComponent(
				process.env.REACT_APP_GOOGLE_REDIRECT_URI || '',
			)
			const state = Math.random().toString(36).substring(2, 15)
			const scope = encodeURIComponent('openid email profile')

			// 구글 로그인 URL 생성
			const googleLoginUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&access_type=offline&prompt=consent`

			console.log(googleLoginUrl)

			// 구글 로그인 페이지로 이동
			// window.location.href = googleLoginUrl
			const popup = window.open(googleLoginUrl, '_blank', 'width=600,height=700')
			if (!popup) {
				throw new Error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.')
			}
		} catch (error) {
			console.error('구글 로그인 오류:', error)
			setErrorMessage('구글 로그인을 실행할 수 없습니다.')
			setIsError(true)
		}
	}, [])

	// 애플 로그인 처리
	const handleAppleLogin = useCallback(() => {
		try {
			const clientId = process.env.REACT_APP_APPLE_CLIENT_ID
			const redirectUri = encodeURIComponent(
				process.env.REACT_APP_APPLE_REDIRECT_URI || '',
			)
			const state = Math.random().toString(36).substring(2, 15)
			const scope = encodeURIComponent('name email')

			// 애플 로그인 URL 생성
			const appleLoginUrl = `https://appleid.apple.com/auth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_mode=form_post`

			// 애플 로그인 페이지로 이동
			window.location.href = appleLoginUrl
		} catch (error) {
			console.error('애플 로그인 오류:', error)
			setErrorMessage('애플 로그인을 실행할 수 없습니다.')
			setIsError(true)
		}
	}, [])

	// window opener postMessage 받기
	const handlePostMessage = useCallback(async (event: MessageEvent) => {
		// 보안을 위해 origin 체크 (개발 시에는 주석 처리)
		// if (event.origin !== process.env.REACT_APP_HOST) return

		const { accessToken, refreshToken, expiryDuration, deviceId } = event.data

		console.log(deviceId)

		if (accessToken && refreshToken && expiryDuration) {
			try {
				sessionStorage.setItem('socialLoginToken', accessToken)

				// 사용자 정보 요청
				const userResponse = await axios.get(API_ENDPOINTS.USER_INFO, {
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				})
				console.log('User info from social login:', userResponse.data)

				// 디바이스 정보 업데이트 요청 (소셜 사용자일 경우...)
				const request = {
					userId: userResponse.data.id,
					userDeviceId: parseInt(deviceId),
					deviceId: uuidv4(),
					deviceType:
						DEVICE_TYPE_MAP[osName as keyof typeof DEVICE_TYPE_MAP] || 'OTHER',
					notificationToken: uuidv4(),
				}

				const deviceResponse = await axios.put(
					API_ENDPOINTS.DEVICE_INFO_UPDATE,
					request,
					{
						headers: {
							Authorization: `Bearer ${accessToken}`,
						},
					},
				)

				// 소셜 로그인 성공 처리
				// 토큰 및 사용자 정보 저장
				const loginResult = {
					accessToken,
					refreshToken,
					expiryDuration,
					tokenType: 'Bearer ',
				}
				setCurrentUser(loginResult)
				dispatch(setUserInfo(userResponse.data))

				// 공통코드 요청
				const commonCodeResponse = await axios.get(API_ENDPOINTS.COMMON_CODE)
				dispatch(setCmmnCd(commonCodeResponse.data))

				setUserDeviceInfo(deviceResponse.data)

				console.log(userResponse.data.roles)

				// userResponse.data.roles 확인하여 권한이 ROLE_SYSTEM 또는 ROLE_ADMIN이 없으면 이용 불가 처리
				const isSystemOrAdmin = userResponse.data.roles.some((role: any) =>
					['ROLE_SYSTEM', 'ROLE_ADMIN'].includes(role.role),
				)
				if (!isSystemOrAdmin) {
					alertModal(
						'일반 사용자 계정은 관리자 도구에 접근할 수 없습니다. 관리자에게 문의하세요.',
						'error',
						() => {
							removeCurrentUser()
							dispatch(removeUserInfo())
							navigate('/', { replace: true })
						},
					)
					return
				}

				// 대시보드로 이동
				navigate('/admin/dashboard', { replace: true })
			} catch (error) {
				console.error('소셜 로그인 처리 중 오류:', error)
				setErrorMessage('소셜 로그인 처리 중 오류가 발생했습니다.')
				setIsError(true)
			}
		}
	}, [])

	useEffect(() => {
		window.addEventListener('message', handlePostMessage)
		return () => {
			window.removeEventListener('message', handlePostMessage)
		}
	}, [handlePostMessage])

	// 로그인 처리 최적화
	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault()

			// 유효성 검사
			if (!loginRequest.username || !loginRequest.password) {
				alertModal('아이디와 비밀번호를 입력해주세요.', 'error', () => {})
				return
			}

			setSubmitDisabled(true)
			clearError()

			try {
				// 로그인 요청
				const loginResponse = await axios.post<LoginResponse>(
					API_ENDPOINTS.LOGIN,
					loginRequest,
				)
				const loginResult = loginResponse.data

				// 사용자 정보 요청
				const userResponse = await axios.get(API_ENDPOINTS.USER_INFO, {
					headers: {
						Authorization: `${loginResult.tokenType}${loginResult.accessToken}`,
					},
				})

				// 토큰 및 사용자 정보 저장
				setCurrentUser(loginResult)
				dispatch(setUserInfo(userResponse.data))

				// 공통코드 요청
				const commonCodeResponse = await axios.get(API_ENDPOINTS.COMMON_CODE)
				dispatch(setCmmnCd(commonCodeResponse.data))

				setUserDeviceInfo(loginRequest.deviceInfo)

				// 대시보드로 이동
				navigate('/admin/dashboard', { replace: true })
			} catch (error) {
				console.error('Login error:', error)

				if (axios.isAxiosError(error)) {
					// const status = error.response?.status
					const errorMessage = error.response?.data?.data || '계정을 확인하세요.'
					if (errorMessage !== '') {
						setErrorMessage(errorMessage)
					}
				} else {
					setErrorMessage('네트워크 오류가 발생했습니다.')
				}

				setIsError(true)
			} finally {
				setSubmitDisabled(false)
			}
		},
		[loginRequest, dispatch, navigate, clearError],
	)

	// 스타일 색상 상수
	const iconColor = useMemo(() => ({ color: 'rgba(255, 255, 255, 0.7)' }), [])
	const errorAlertStyles = useMemo(
		() => ({
			mb: 2,
			background: 'rgba(244, 67, 54, 0.1)',
			border: '1px solid rgba(244, 67, 54, 0.3)',
			color: '#ff6b6b',
			'& .MuiAlert-icon': {
				color: '#ff6b6b',
			},
		}),
		[],
	)

	const [alreadyLoggedIn, setAlreadyLoggedIn] = useState(false)

	const [termInfo, setTermInfo] = useState<{
		privacyPolicy: string
		termsOfService: string
	}>({
		privacyPolicy: '',
		termsOfService: '',
	})
	const [termModalType, setTermModalType] = useState<
		'privacyPolicy' | 'termsOfService' | null
	>(null)

	const handleOpenTermModal = useCallback(
		(type: 'privacyPolicy' | 'termsOfService') => {
			setTermModalType(type)
		},
		[],
	)

	const handleCloseTermModal = useCallback(() => {
		setTermModalType(null)
	}, [])

	const getTerm = () => {
		axios.get(API_ENDPOINTS.TERM).then((response) => {
			const data = response.data
			setTermInfo({
				privacyPolicy: data.privacyPolicy,
				termsOfService: data.termsOfService,
			})
		})
	}

	useEffect(() => {
		getTerm()
	}, [])

	// 자동 로그인 처리
	useEffect(() => {
		const userInfo = getCurrentUser()

		console.log(userInfo)

		if (userInfo && !alreadyLoggedIn) {
			const me = async () => {
				try {
					// 사용자 정보 요청
					const userResponse = await instance.get(API_ENDPOINTS.USER_INFO)

					console.log('자동 로그인 사용자 정보:', userResponse.data.roles)

					// userResponse.data.roles 확인하여 권한이 ROLE_USER만 있으면 이용 불가 처리
					const isSystemOrAdmin = userResponse.data.roles.some((role: any) =>
						['ROLE_SYSTEM', 'ROLE_ADMIN'].includes(role.role),
					)
					console.log(isSystemOrAdmin)
					if (!isSystemOrAdmin) {
						alertModal(
							'일반 사용자 계정은 관리자 도구에 접근할 수 없습니다. 관리자에게 문의하세요.',
							'error',
							() => {
								removeCurrentUser()
								dispatch(removeUserInfo())
								navigate('/', { replace: true })
							},
						)
						return
					}

					// 사용자 정보 Redux 저장
					dispatch(setUserInfo(userResponse.data))

					// 공통코드 요청
					const commonCodeResponse = await instance.get(API_ENDPOINTS.COMMON_CODE)
					dispatch(setCmmnCd(commonCodeResponse.data))

					setAlreadyLoggedIn(true)

					// 대시보드로 이동
					// navigate('/admin/dashboard', { replace: true })
				} catch (error) {
					console.error('자동 로그인 오류:', error)
					// 토큰이 유효하지 않은 경우 로그인 페이지로 리다이렉트
					setAlreadyLoggedIn(false)
				}
			}

			me()
		}
	}, [alreadyLoggedIn, navigate])

	const eventSourceRef = useRef<EventSource | null>(null)
	/**
	 * 로그아웃
	 */
	const logoutAction = () => {
		console.log(userInfo)
		const deviceInfo: DeviceInfoModel = getUserDeviceInfo()
		console.log(deviceInfo)
		confirmAlert(
			'로그아웃 하시겠습니까?',
			() => {
				apiLogout(deviceInfo).then(() => {
					removeCurrentUser()
					dispatch(removeUserInfo())
					eventSourceRef.current?.close()
					navigate('/')
				})
			},
			() => {},
		)
	}

	return (
		<LoginContainer
			maxWidth={false}
			disableGutters
		>
			<GridBackground />
			{!alreadyLoggedIn ? (
				<LoginPaper elevation={0}>
					<Box
						component="form"
						onSubmit={handleSubmit}
						sx={{ width: '100%' }}
					>
						{/* 로고 및 제목 */}
						<TechTypography
							variant="h4"
							sx={{ letterSpacing: '-0.15rem' }}
						>
							{process.env.REACT_APP_TITLE}
						</TechTypography>
						<Typography
							variant="body2"
							sx={{
								color: 'rgba(255, 255, 255, 0.7)',
								textAlign: 'center',
								mb: 4,
								fontSize: '14px',
							}}
						>
							Admin Tool
						</Typography>

						{/* 에러 메시지 */}
						{isError && (
							<Alert
								severity="error"
								sx={errorAlertStyles}
							>
								{errorMessage}
							</Alert>
						)}

						{/* 아이디 입력 */}
						<Box sx={{ mb: 2 }}>
							<TechTextField
								fullWidth
								id="username"
								name="username"
								label="아이디"
								type="text"
								autoComplete="username"
								onChange={handleInputChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<PersonIcon sx={iconColor} />
										</InputAdornment>
									),
								}}
							/>
						</Box>

						{/* 비밀번호 입력 */}
						<Box sx={{ mb: 3 }}>
							<TechTextField
								fullWidth
								id="password"
								name="password"
								label="비밀번호"
								type={showPassword ? 'text' : 'password'}
								autoComplete="current-password"
								onChange={handleInputChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<LockIcon sx={iconColor} />
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={togglePasswordVisibility}
												edge="end"
												sx={iconColor}
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</Box>

						{/* 로그인 버튼 */}
						<Button
							type="submit"
							fullWidth
							variant="contained"
							disabled={submitDisabled}
							sx={{ mb: 2 }}
						>
							{submitDisabled ? '로그인 중...' : '로그인'}
						</Button>

						<Divider sx={{ backgroundColor: '#ccc' }} />

						<Box
							sx={{ my: 2, display: 'flex', justifyContent: 'space-between', gap: 2 }}
						>
							<button
								type="button"
								onClick={() => handleOpenTermModal('privacyPolicy')}
								style={{
									border: 0,
									outline: 0,
									backgroundColor: 'transparent',
									color: 'rgba(255, 255, 255, 0.5)',
									textDecoration: 'underline',
								}}
							>
								개인정보처리방침
							</button>
							<button
								type="button"
								onClick={() => handleOpenTermModal('termsOfService')}
								style={{
									border: 0,
									outline: 0,
									backgroundColor: 'transparent',
									color: 'rgba(255, 255, 255, 0.5)',
									textDecoration: 'underline',
								}}
							>
								이용약관
							</button>
						</Box>

						<Divider sx={{ backgroundColor: '#ccc' }} />

						{/* 추가 정보 */}
						<Typography
							variant="caption"
							sx={{
								color: 'rgba(255, 255, 255, 0.5)',
								textAlign: 'center',
								display: 'block',
								mt: 2,
							}}
						>
							상호명 : 주식회사 올인어스 (AllinUs Co.,Ltd.)
							<br />
							대표 : 김영준
							<br />
							사업장 소재지 : 서울특별시 강남구 봉은사로 317, 2층 2186호(논현동,
							아모제논현빌딩)
							<br />
							사업자등록번호 : 176-81-03910
							<br />
							<br />© 2025
							{process.env.REACT_APP_TITLE}. All rights reserved.
						</Typography>

						<Dialog
							open={Boolean(termModalType)}
							onClose={handleCloseTermModal}
							fullWidth
							maxWidth="md"
						>
							<DialogTitle>
								{termModalType === 'privacyPolicy' ? '개인정보처리방침' : '이용약관'}
							</DialogTitle>
							<DialogContent dividers>
								<div
									dangerouslySetInnerHTML={{
										__html: termModalType ? termInfo[termModalType] : '',
									}}
								></div>
							</DialogContent>
							<DialogActions>
								<Button onClick={handleCloseTermModal}>닫기</Button>
							</DialogActions>
						</Dialog>
					</Box>
				</LoginPaper>
			) : (
				<LoginPaper elevation={0}>
					<TechTypography variant="h4">{process.env.REACT_APP_TITLE}</TechTypography>
					<Typography
						variant="body2"
						sx={{
							color: 'rgba(255, 255, 255, 0.7)',
							textAlign: 'center',
							mb: 4,
							fontSize: '14px',
						}}
					>
						Admin Tool
					</Typography>
					<AccountContainer sx={{ gap: 2 }}>
						<Button
							variant="contained"
							component={Link}
							to="/admin/dashboard"
							fullWidth
						>
							대시보드로 이동
						</Button>
						<Button
							onClick={logoutAction}
							variant="contained"
							color="warning"
							fullWidth
						>
							로그아웃
						</Button>
					</AccountContainer>
				</LoginPaper>
			)}
		</LoginContainer>
	)
}
export default Login
